import React, { useState, useEffect, Suspense, lazy } from 'react';

// --- ALWAYS EAGER: first paint or exports hooks used at top level ---
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import PublicLayout from './components/PublicLayout';
import AchievementSystem, { useAchievementToasts, calcXPFromProgress, xpProgress } from './components/AchievementSystem';
import ErrorBoundary from './components/ErrorBoundary';
import { InstallBanner, useNotifications, useTabTitleBadge, StreakBanner } from './components/PWAManager';
import RedesignBanner from './components/RedesignBanner';
import { CheckCircle, AlertCircle, X, Sun, Moon, Monitor } from 'lucide-react';

// --- FIREBASE IMPORTS ---
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, onSnapshot, query, where, getDocs, deleteField, increment } from 'firebase/firestore';

// --- ROUTER IMPORTS ---
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// --- XP SYSTEM ---
import { calcXP, awardXP } from './utils/xp';

// --- LAZY: auth/onboarding components only rendered conditionally ---
const Login = lazy(() => import('./components/Login'));
const Signup = lazy(() => import('./components/Signup'));
const Onboarding = lazy(() => import('./components/Onboarding'));

// --- LAZY LOADED: each becomes its own chunk ---
const LandingPage = lazy(() => import('./components/LandingPage'));
const VocabList = lazy(() => import('./components/VocabList'));
const KanjiList = lazy(() => import('./components/KanjiList'));
const GrammarPractice = lazy(() => import('./components/GrammarPractice'));
const ReadingPractice = lazy(() => import('./components/ReadingPractice'));
const Quiz = lazy(() => import('./components/Quiz'));
const StudyPlans = lazy(() => import('./components/StudyPlans'));
const MyDictionary = lazy(() => import('./components/MyDictionary'));
const DrillTest = lazy(() => import('./components/DrillTest'));
const SRSReview = lazy(() => import('./components/SRSReview'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const SettingsPanel = lazy(() => import('./components/SettingsPanel'));
const CommandPalette = lazy(() => import('./components/CommandPalette'));
const WelcomeGuide = lazy(() => import('./components/WelcomeGuide'));
const PlacementTest = lazy(() => import('./components/PlacementTest'));
const ShareProgressCard = lazy(() => import('./components/ShareProgressCard'));
const StudyTimer = lazy(() => import('./components/StudyTimer'));
const StrokeCanvas = lazy(() => import('./components/StrokeCanvas'));
const VocabInContext = lazy(() => import('./components/VocabInContext'));
const ListeningQuiz = lazy(() => import('./components/ListeningQuiz'));
const RefundPolicyPage = lazy(() => import('./components/RefundPolicyPage'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const OurStoryPage = lazy(() => import('./components/OurStoryPage'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const ArticlePage = lazy(() => import('./components/ArticlePage'));
const JapanExplorerPage = lazy(() => import('./components/JapanExplorerPage'));
const JapanEventsPage = lazy(() => import('./components/JapanEventsPage'));
const OnomatopoeiaPage = lazy(() => import('./components/OnomatopoeiaPage'));
const VerbPairsPage = lazy(() => import('./components/VerbPairsPage'));
const ShopPage = lazy(() => import('./components/ShopPage'));

const THEME_CYCLE = { day: 'night', night: 'auto', auto: 'day' };
const THEME_ICON  = { day: Sun, night: Moon, auto: Monitor };
const THEME_LABEL = { day: 'Switch to Night', night: 'Switch to Auto', auto: 'Switch to Day' };

function FloatingThemeBtn({ currentTheme, onThemeChange }) {
  const mode = currentTheme || 'night';
  const Icon = THEME_ICON[mode] || Moon;
  return (
    <div
      className="md:hidden"
      style={{ position: 'fixed', top: '13px', right: '58px', zIndex: 9990 }}
    >
      <button
        onClick={() => onThemeChange(THEME_CYCLE[mode] || 'night')}
        title={THEME_LABEL[mode]}
        aria-label={THEME_LABEL[mode]}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: '#1a1a1a',
          border: '2px solid #04EC01',
          color: '#04EC01',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 12px rgba(4,236,1,0.25)',
          transition: 'transform 0.18s ease, box-shadow 0.18s ease',
          flexShrink: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(4,236,1,0.4)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = '0 2px 12px rgba(4,236,1,0.25)'; }}
      >
        <Icon size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authView, setAuthView] = useState('login');
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [redirectAuthError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [guestMode, setGuestMode] = useState(() => localStorage.getItem('go4nihongo_guest_mode') === '1');
  const [showGuestPaywall, setShowGuestPaywall] = useState(false);
  const [paywallLevel, setPaywallLevel] = useState(null);
  const [guestBannerDismissed, setGuestBannerDismissed] = useState(() => localStorage.getItem('go4nihongo_guest_banner_dismissed') === '1');
  const [userNickname, setUserNickname] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showPublicPlans, setShowPublicPlans] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('go4nihongo_welcome_seen'));
  const [showPlacementTest, setShowPlacementTest] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [currentModule, setCurrentModule] = useState('vocab');
  const [selectedLevel, setSelectedLevel] = useState('N5');
  const [currentLang, setCurrentLang] = useState('en');

  const [showCommandPalette, setShowCommandPalette] = useState(false);

  const [masteredVocab, setMasteredVocab] = useState([]);
  const [masteredKanji, setMasteredKanji] = useState([]);
  const [masteredGrammar, setMasteredGrammar] = useState([]);
  const [masteredReading, setMasteredReading] = useState([]);
  const [readingAttempts, setReadingAttempts] = useState({});
  const [quizMode, setQuizMode] = useState('unmastered');
  const [quizSubject, setQuizSubject] = useState('vocab'); 
  
  const [userPlan, setUserPlan]         = useState('free');
  const [streak, setStreak]             = useState(0);
  const [lastActiveDate, setLastActiveDate] = useState(null);
  const [srsProgress, setSrsProgress]       = useState({});
  const [drillProgress, setDrillProgress]   = useState({});
  const [activityLog, setActivityLog]       = useState({});   // { 'YYYY-MM-DD': count }
  const [quizScores, setQuizScores]         = useState({});   // { N5: { bestPct, sessions } }
  const [totalStudySecs, setTotalStudySecs] = useState(0);
  const sessionStartRef = React.useRef(null);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [showStrokeCanvas, setShowStrokeCanvas] = useState(false);
  const [showVocabContext, setShowVocabContext] = useState(false);
  const [showListeningQuiz, setShowListeningQuiz] = useState(false);
  const [showToolbar, setShowToolbar]     = useState(false);
  const [toolbarPos, setToolbarPos]       = useState({ right: 16, bottom: 24 });
  const [toolbarDragging, setToolbarDragging] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Theme mode: 'day' | 'night' | 'auto'. Resolves to data-theme = 'go4-light' or 'go4-dark'.
  const [currentTheme, setCurrentTheme] = useState(() => {
    // One-time migration from old theme-id storage (shukan-stamp / sumi-matcha / study-os / harajuku-spread / go4-dark)
    if (!localStorage.getItem('go4nihongo_theme_mode_migrated_v1')) {
      const old = localStorage.getItem('go4nihongo_theme');
      const mode = (old === 'go4-dark' || old === 'study-os' || old === 'harajuku-spread') ? 'night' : 'day';
      localStorage.setItem('go4nihongo_theme_mode', mode);
      localStorage.setItem('go4nihongo_theme_mode_migrated_v1', '1');
      return mode;
    }
    return localStorage.getItem('go4nihongo_theme_mode') || 'day';
  });

  // Resolve mode → actual theme ID. 'auto' reads system preference.
  const resolveTheme = (mode) => {
    if (mode === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'go4-dark' : 'go4-light';
    }
    return mode === 'night' ? 'go4-dark' : 'go4-light';
  };

  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(currentTheme));
  const isDarkMode = resolvedTheme === 'go4-dark';

  useEffect(() => {
    setResolvedTheme(resolveTheme(currentTheme));
    localStorage.setItem('go4nihongo_theme_mode', currentTheme);

    // Live-update when user is on 'auto' and their OS theme flips
    if (currentTheme !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setResolvedTheme(resolveTheme('auto'));
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [currentTheme]);

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', resolvedTheme);
    if (resolvedTheme === 'go4-dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Persist guest mode + load guest progress from localStorage
  useEffect(() => {
    localStorage.setItem('go4nihongo_guest_mode', guestMode ? '1' : '0');
    if (guestMode && !user) {
      try {
        setMasteredVocab(JSON.parse(localStorage.getItem('go4nihongo_guest_vocab') || '[]'));
        setMasteredKanji(JSON.parse(localStorage.getItem('go4nihongo_guest_kanji') || '[]'));
        setMasteredGrammar(JSON.parse(localStorage.getItem('go4nihongo_guest_grammar') || '[]'));
        setMasteredReading(JSON.parse(localStorage.getItem('go4nihongo_guest_reading') || '[]'));
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestMode]);

  // Save guest progress on change
  useEffect(() => {
    if (guestMode && !user) {
      localStorage.setItem('go4nihongo_guest_vocab', JSON.stringify(masteredVocab));
      localStorage.setItem('go4nihongo_guest_kanji', JSON.stringify(masteredKanji));
      localStorage.setItem('go4nihongo_guest_grammar', JSON.stringify(masteredGrammar));
      localStorage.setItem('go4nihongo_guest_reading', JSON.stringify(masteredReading));
    }
  }, [masteredVocab, masteredKanji, masteredGrammar, masteredReading, guestMode, user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success' || payment === 'cancelled') {
      setPaymentStatus(payment);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!paymentStatus) return;
    const timer = setTimeout(() => setPaymentStatus(null), paymentStatus === 'cancelled' ? 15000 : 6000);
    return () => clearTimeout(timer);
  }, [paymentStatus]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault(); 
        setShowCommandPalette((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Safety net: if Firebase hangs (App Check slow on mobile), unblock the UI after 8s
  useEffect(() => {
    const timeout = setTimeout(() => setAuthLoading(false), 8000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let unsubscribeSubs = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      // Block email/password users who haven't verified their email yet
      if (currentUser) {
        const isEmailPassword = currentUser.providerData.some(p => p.providerId === 'password');
        if (isEmailPassword && !currentUser.emailVerified) {
          await signOut(auth);
          setAuthLoading(false);
          return;
        }
      }

      setUser(currentUser);

      if (currentUser) {
        // --- 1. USER PROFILE & STREAK LOGIC ---
        const userDocRef = doc(db, 'users', currentUser.uid);
        let userDocSnap;
        try {
          userDocSnap = await Promise.race([
            getDoc(userDocRef),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 6000))
          ]);
        } catch (e) {
          console.warn('User doc fetch timed out or failed, continuing:', e);
          setAuthLoading(false);
          return;
        }

        const today = new Date().toLocaleDateString('en-CA'); 
        const yesterdayDate = new Date();
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const yesterday = yesterdayDate.toLocaleDateString('en-CA');

        if (!userDocSnap.exists()) {
          await setDoc(userDocRef, { email: currentUser.email, currentStreak: 1, lastActiveDate: today });
          setStreak(1);
          setLastActiveDate(today);
          setShowOnboarding(true);
        } else {
          const userData = userDocSnap.data();
          if (userData.nickname) setUserNickname(userData.nickname);
          if (!userData.onboardingComplete) setShowOnboarding(true);
          let newStreak = userData.currentStreak || 0;
          let lastActive = userData.lastActiveDate || null;

          if (lastActive === yesterday) {
            newStreak += 1;
            await updateDoc(userDocRef, { currentStreak: newStreak, lastActiveDate: today });
            awardXP(currentUser.uid, calcXP.streak(), userData.nickname || '');
          } else if (lastActive !== today) {
            newStreak = 1;
            await updateDoc(userDocRef, { currentStreak: newStreak, lastActiveDate: today });
            awardXP(currentUser.uid, calcXP.streak(), userData.nickname || '');
          }
          setStreak(newStreak);
          setLastActiveDate(userData.lastActiveDate || null);
          setActivityLog(userData.activityLog || {});
          setTotalStudySecs(userData.totalStudySecs || 0);
        }

        // --- 2. HIGH-SPEED, SRS-READY PROGRESS FETCH ---
        try {
          const progressSnap = await getDocs(collection(db, 'users', currentUser.uid, 'progress'));

          let allMasteredVocab = [];
          let allMasteredKanji = [];
          let allMasteredGrammar = [];
          let allMasteredReading = [];
          let allReadingAttempts = {};
          let fullSrsProgress = {};

          let fullDrillProgress = {};
          let fullQuizScores   = {};

          progressSnap.forEach((docSnap) => {
            const levelData = docSnap.data();
            const level = docSnap.id;

            // Store the full document for SRS scheduling
            fullSrsProgress[level] = levelData;

            // Store drill progress
            if (levelData.drill) fullDrillProgress[level] = levelData.drill;

            // Store quiz scores
            if (levelData.quiz) fullQuizScores[level] = levelData.quiz;

            if (levelData.vocab) {
              const vocabIds = Object.keys(levelData.vocab).filter(id => levelData.vocab[id].status === 'mastered');
              allMasteredVocab.push(...vocabIds);
            }

            if (levelData.kanji) {
              const kanjiIds = Object.keys(levelData.kanji).filter(id => levelData.kanji[id].status === 'mastered');
              allMasteredKanji.push(...kanjiIds);
            }

            if (levelData.grammar) {
              const grammarIds = Object.keys(levelData.grammar).filter(id => levelData.grammar[id].status === 'mastered');
              allMasteredGrammar.push(...grammarIds);
            }

            if (levelData.reading) {
              Object.entries(levelData.reading).forEach(([id, data]) => {
                if (data.status === 'mastered') allMasteredReading.push(id);
                if (data.attempts > 0) allReadingAttempts[id] = data.attempts;
              });
            }
          });

          setMasteredVocab(allMasteredVocab);
          setMasteredKanji(allMasteredKanji);
          setMasteredGrammar(allMasteredGrammar);
          setMasteredReading(allMasteredReading);
          setReadingAttempts(allReadingAttempts);
          setSrsProgress(fullSrsProgress);
          setDrillProgress(fullDrillProgress);
          setQuizScores(fullQuizScores);
        } catch (err) {
          console.error("Error fetching progress data:", err);
          setMasteredVocab([]);
          setMasteredKanji([]);
        }

        // --- 3. SUBSCRIPTION LOGIC (Remains unchanged) ---
        try {
          const subsRef = collection(db, 'users', currentUser.uid, 'subscriptions');
          const q = query(subsRef, where('status', 'in', ['trialing', 'active']));
          
          unsubscribeSubs = onSnapshot(q, (subSnaps) => {
            const plans = [];
            subSnaps.forEach((subscriptionDoc) => {
              const data = subscriptionDoc.data();
              if (data.role) plans.push(String(data.role).toLowerCase());
              else if (data.items && data.items.length > 0) {
                const nestedRole = data.items[0].price?.product?.metadata?.firebaseRole;
                if (nestedRole) plans.push(String(nestedRole).toLowerCase());
              }
            });

            let finalPlanString = 'free';
            if (plans.includes('mastery_bundle')) finalPlanString = 'mastery_bundle,n1_plus,n2_plus,n3_plus,n4_plus,n5_plus';
            else if (plans.includes('n1_plus')) finalPlanString = 'n1_plus,n2_plus,n3_plus,n4_plus,n5_plus';
            else if (plans.includes('n2_plus')) finalPlanString = 'n2_plus,n3_plus,n4_plus,n5_plus';
            else if (plans.includes('n3_plus')) finalPlanString = 'n3_plus,n4_plus,n5_plus';
            else if (plans.includes('n4_plus')) finalPlanString = 'n4_plus,n5_plus';
            else if (plans.includes('n5_plus')) finalPlanString = 'n5_plus';

            setUserPlan(finalPlanString);
          });
        } catch (err) {
          setUserPlan('free');
        }

      } else {
        setMasteredVocab([]);
        setMasteredKanji([]);
        setUserPlan('free');
        setStreak(0);
        unsubscribeSubs(); 
      }
      setAuthLoading(false); 
    });
    
    return () => {
      unsubscribeAuth();
      unsubscribeSubs();
    };
  }, []);

  // --- LEVEL-BASED, SRS-READY FIREBASE WRITES ---

  const SRS_INTERVALS_APP = [1, 3, 7, 14, 30, 90];

  const getNextSRSInterval = (current, passed) => {
    if (!passed) return 1;
    const idx = SRS_INTERVALS_APP.indexOf(current);
    if (idx === -1 || idx === SRS_INTERVALS_APP.length - 1) return 90;
    return SRS_INTERVALS_APP[idx + 1];
  };

  const updateSrsLocal = (level, type, stringId, fields) => {
    setSrsProgress(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [type]: {
          ...(prev[level]?.[type] || {}),
          [stringId]: {
            ...(prev[level]?.[type]?.[stringId] || {}),
            ...fields,
          },
        },
      },
    }));
  };

  const removeSrsLocal = (level, type, stringId) => {
    setSrsProgress(prev => {
      const updated = { ...prev };
      if (updated[level]?.[type]) {
        const copy = { ...updated[level][type] };
        delete copy[stringId];
        updated[level] = { ...updated[level], [type]: copy };
      }
      return updated;
    });
  };

  const handleToggleVocabMastery = async (id, isMastering) => {
    const stringId = String(id);
    const level = stringId.split('-')[0];
    const now = Date.now();

    setMasteredVocab(prev => isMastering ? [...prev, stringId] : prev.filter(vId => vId !== stringId));

    if (isMastering) {
      updateSrsLocal(level, 'vocab', stringId, { status: 'mastered', lastReviewed: now, interval: 1, dueDate: now + 86400000 });
      handleRecordActivity();
    } else {
      removeSrsLocal(level, 'vocab', stringId);
    }

    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      await setDoc(levelRef, {
        vocab: {
          [stringId]: isMastering
            ? { status: 'mastered', lastReviewed: now, interval: 1, dueDate: now + 86400000 }
            : deleteField()
        }
      }, { merge: true });
      if (isMastering) awardXP(user.uid, calcXP.vocab(level), userNickname);
    }
  };

  const handleToggleKanjiMastery = async (id, isMastering) => {
    const stringId = String(id);
    const level = stringId.split('-')[0];
    const now = Date.now();

    setMasteredKanji(prev => isMastering ? [...prev, stringId] : prev.filter(kId => kId !== stringId));

    if (isMastering) {
      updateSrsLocal(level, 'kanji', stringId, { status: 'mastered', lastReviewed: now, interval: 1, dueDate: now + 86400000 });
      handleRecordActivity();
    } else {
      removeSrsLocal(level, 'kanji', stringId);
    }

    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      await setDoc(levelRef, {
        kanji: {
          [stringId]: isMastering
            ? { status: 'mastered', lastReviewed: now, interval: 1, dueDate: now + 86400000 }
            : deleteField()
        }
      }, { merge: true });
      if (isMastering) awardXP(user.uid, calcXP.kanji(level), userNickname);
    }
  };

  const handleToggleGrammarMastery = async (id, isMastering) => {
    const stringId = String(id);
    const level = stringId.split('-')[0];
    const now = Date.now();

    setMasteredGrammar(prev => isMastering ? [...prev, stringId] : prev.filter(gId => gId !== stringId));

    if (isMastering) {
      updateSrsLocal(level, 'grammar', stringId, { status: 'mastered', lastReviewed: now, interval: 1, dueDate: now + 86400000 });
    } else {
      removeSrsLocal(level, 'grammar', stringId);
    }

    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      await setDoc(levelRef, {
        grammar: {
          [stringId]: isMastering
            ? { status: 'mastered', lastReviewed: now, interval: 1, dueDate: now + 86400000 }
            : deleteField()
        }
      }, { merge: true });
      if (isMastering) awardXP(user.uid, calcXP.grammar(level), userNickname);
    }
  };

  const handleSRSReview = async (id, type, passed) => {
    const stringId = String(id);
    const level = stringId.split('-')[0];
    const now = Date.now();

    const currentInterval = srsProgress[level]?.[type]?.[stringId]?.interval || 1;
    const nextInterval = getNextSRSInterval(currentInterval, passed);
    const nextDueDate = now + nextInterval * 24 * 60 * 60 * 1000;

    updateSrsLocal(level, type, stringId, { lastReviewed: now, interval: nextInterval, dueDate: nextDueDate });
    setReviewCount(prev => prev + 1);

    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      await setDoc(levelRef, {
        [type]: {
          [stringId]: { lastReviewed: now, interval: nextInterval, dueDate: nextDueDate }
        }
      }, { merge: true });
    }
  };

  const handleToggleReadingMastery = async (id, isMastering) => {
    const stringId = String(id);
    const level = stringId.split('-')[0];

    setMasteredReading(prev => isMastering ? [...prev, stringId] : prev.filter(rId => rId !== stringId));

    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      await setDoc(levelRef, {
        reading: {
          [stringId]: isMastering ? {
            status: 'mastered',
            lastReviewed: Date.now()
          } : deleteField()
        }
      }, { merge: true });
      if (isMastering) awardXP(user.uid, calcXP.reading(level), userNickname);
    }
  };

  const handleRecordReadingAttempt = async (id) => {
    const stringId = String(id);
    const level = stringId.split('-')[0];
    setReadingAttempts(prev => ({ ...prev, [stringId]: (prev[stringId] || 0) + 1 }));
    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      await setDoc(levelRef, {
        reading: { [stringId]: { attempts: increment(1) } }
      }, { merge: true });
    }
  };

  const handleResetVocab = async (levelToReset) => {
    setMasteredVocab(prev => prev.filter(id => !id.startsWith(`${levelToReset}-`)));
    
    if (user) {
      await setDoc(doc(db, 'users', user.uid, 'progress', levelToReset), {
        vocab: {} // Wipes the entire vocab object clean for this level
      }, { merge: true });
    }
  };

  const handleResetKanji = async (levelToReset) => {
    setMasteredKanji(prev => prev.filter(id => !id.startsWith(`${levelToReset}-`)));
    if (user) {
      await setDoc(doc(db, 'users', user.uid, 'progress', levelToReset), {
        kanji: {}
      }, { merge: true });
    }
  };

  const handleResetGrammar = async (levelToReset) => {
    setMasteredGrammar(prev => prev.filter(id => !id.startsWith(`${levelToReset}-`)));
    if (user) {
      await setDoc(doc(db, 'users', user.uid, 'progress', levelToReset), {
        grammar: {}
      }, { merge: true });
    }
  };

  // ── Activity log (heatmap) ──────────────────────────────────────────────
  const handleRecordActivity = async () => {
    if (!user) return;
    const today = new Date().toLocaleDateString('en-CA');
    setActivityLog(prev => ({ ...prev, [today]: (prev[today] || 0) + 1 }));
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        [`activityLog.${today}`]: increment(1),
      });
    } catch { /* silent */ }
  };

  // ── Quiz score tracking ─────────────────────────────────────────────────
  const handleQuizComplete = async (level, score, total) => {
    if (!level || total === 0) return;
    const pct = Math.round((score / total) * 100);
    const prev = quizScores[level] || { bestPct: 0, sessions: 0 };
    const updated = { bestPct: Math.max(prev.bestPct, pct), sessions: (prev.sessions || 0) + 1, lastPlayed: Date.now() };
    setQuizScores(q => ({ ...q, [level]: updated }));
    handleRecordActivity();
    if (user) {
      await setDoc(doc(db, 'users', user.uid, 'progress', level), { quiz: updated }, { merge: true });
      awardXP(user.uid, calcXP.quiz(pct), userNickname);
    }
  };

  // ── Study time tracking ─────────────────────────────────────────────────
  const saveStudyTime = async () => {
    if (!user || !sessionStartRef.current) return;
    const secs = Math.round((Date.now() - sessionStartRef.current) / 1000);
    sessionStartRef.current = null;
    if (secs < 30) return; // ignore micro-sessions
    setTotalStudySecs(prev => prev + secs);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        totalStudySecs: increment(secs),
      });
    } catch { /* silent */ }
  };

  const handleDrillComplete = async (level, modes, score, total) => {
    if (!level || !modes?.length) return;
    const pct = total > 0 ? Math.round((score / total) * 100) : 0;
    const now = Date.now();

    // Optimistic local update
    setDrillProgress(prev => {
      const levelDrills = { ...(prev[level] || {}) };
      modes.forEach(mode => {
        levelDrills[mode] = {
          sessions:  (levelDrills[mode]?.sessions  || 0) + 1,
          bestPct:   Math.max(levelDrills[mode]?.bestPct || 0, pct),
          lastPlayed: now,
        };
      });
      return { ...prev, [level]: levelDrills };
    });

    if (user) {
      const levelRef = doc(db, 'users', user.uid, 'progress', level);
      const modeData = {};
      modes.forEach(mode => {
        modeData[mode] = {
          sessions:   increment(1),
          bestPct:    Math.max(drillProgress[level]?.[mode]?.bestPct || 0, pct),
          lastPlayed: now,
        };
      });
      await setDoc(levelRef, { drill: modeData }, { merge: true });
      awardXP(user.uid, calcXP.drill() * modes.length, userNickname);
    }
  };

  const handleLevelSelect = (level, moduleName = 'vocab') => {
    // Guests can only access N5. Anything else triggers the paywall.
    if (!user && level !== 'N5' && level !== 'Extras') {
      setPaywallLevel(level);
      setShowGuestPaywall(true);
      return;
    }
    saveStudyTime(); // save previous session before switching
    sessionStartRef.current = Date.now();
    setSelectedLevel(level);
    setCurrentView('practice');
    setCurrentModule(moduleName);
  };

  const handleStartQuiz = (mode, subject) => {
    setQuizMode(mode);
    setQuizSubject(subject);
    setCurrentModule('quiz');
  };

  const handleCommandNavigation = (view, moduleName) => {
    setCurrentView(view);
    if (moduleName) setCurrentModule(moduleName);
  };

  // Total SRS due count (all levels) for PWA badge + notification
  const totalDueReviewCount = (() => {
    const now = Date.now();
    let count = 0;
    Object.values(srsProgress).forEach(ld => {
      ['vocab','kanji','grammar'].forEach(t => {
        if (ld[t]) count += Object.values(ld[t]).filter(i => i.status === 'mastered' && (!i.dueDate || i.dueDate <= now)).length;
      });
    });
    return count;
  })();

  // PWA hooks
  useTabTitleBadge(totalDueReviewCount);
  useNotifications({ dueReviewCount: totalDueReviewCount, streak, user });

  // Achievement toast detection
  const achievementStats = {
    vocab: masteredVocab.length,
    kanji: masteredKanji.length,
    grammar: masteredGrammar.length,
    streak,
    reviews: reviewCount,
    total: masteredVocab.length + masteredKanji.length + masteredGrammar.length,
  };
  const AchievementToastEl = useAchievementToasts(achievementStats);

  // Content-aware skeleton loader — mimics the hero header + card grid layout
  const ModuleLoader = () => (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-4 animate-pulse">
      {/* Hero header skeleton */}
      <div className="rounded-2xl bg-slate-200 dark:bg-slate-800 h-36 mb-4 overflow-hidden relative">
        <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent animate-[shimmer_1.4s_infinite]" />
      </div>
      {/* Search bar skeleton */}
      <div className="h-8 bg-slate-100 dark:bg-slate-800/60 rounded-xl mb-5 w-1/2" />
      {/* Card grid skeleton — 3 columns */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="rounded-2xl bg-slate-100 dark:bg-slate-800/60 overflow-hidden relative" style={{ height: '22rem' }}>
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 dark:via-white/5 to-transparent animate-[shimmer_1.4s_infinite]"
              style={{ animationDelay: `${i * 0.1}s` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderModule = () => {
    if (selectedLevel === 'Extras') {
      return <BlogPage />;
    }
    switch (currentModule) {
      case 'vocab': 
        return <VocabList currentLevel={selectedLevel} currentLang={currentLang} masteredVocab={masteredVocab} onResetVocab={() => handleResetVocab(selectedLevel)} onStartQuiz={(mode) => handleStartQuiz(mode, 'vocab')} userPlan={userPlan} onUpgradeClick={() => setCurrentModule('plans')} onToggleMastery={handleToggleVocabMastery} activityLog={activityLog} />;
      case 'kanji': 
        return <KanjiList currentLevel={selectedLevel} currentLang={currentLang} masteredKanji={masteredKanji} onResetKanji={() => handleResetKanji(selectedLevel)} onStartQuiz={(mode) => handleStartQuiz(mode, 'kanji')} userPlan={userPlan} onUpgradeClick={() => setCurrentModule('plans')} onToggleMastery={handleToggleKanjiMastery} activityLog={activityLog} />;
      case 'grammar': 
        return <GrammarPractice currentLevel={selectedLevel} currentLang={currentLang} userPlan={userPlan} onUpgradeClick={() => setCurrentModule('plans')} onStartQuiz={(mode) => handleStartQuiz(mode, 'grammar')} masteredGrammar={masteredGrammar} onToggleGrammarMastery={handleToggleGrammarMastery} onResetGrammar={() => handleResetGrammar(selectedLevel)} activityLog={activityLog} />;
      case 'reading': 
        return <ReadingPractice currentLevel={selectedLevel} currentLang={currentLang} userPlan={userPlan} onUpgradeClick={() => setCurrentModule('plans')} masteredReading={masteredReading} onToggleReadingMastery={handleToggleReadingMastery} onRecordReadingAttempt={handleRecordReadingAttempt} activityLog={activityLog} />;
      case 'quiz':
        return <Quiz
          currentLevel={selectedLevel}
          currentLang={currentLang}
          onMarkMastered={quizSubject === 'vocab' ? handleToggleVocabMastery : (quizSubject === 'kanji' ? handleToggleKanjiMastery : null)}
          masteredItems={quizSubject === 'vocab' ? masteredVocab : (quizSubject === 'kanji' ? masteredKanji : [])}
          initialFilterMode={quizMode}
          quizSubject={quizSubject}
          onQuizComplete={(score, total) => handleQuizComplete(selectedLevel, score, total)}
          activityLog={activityLog}
        />;
      case 'review':
        return <SRSReview currentLevel={selectedLevel} currentLang={currentLang} srsProgress={srsProgress} onReview={handleSRSReview} onExit={() => setCurrentModule('vocab')} />;
      case 'drill':
        return <DrillTest currentLevel={selectedLevel} userLanguage={currentLang} onExit={() => setCurrentModule('vocab')} onDrillComplete={handleDrillComplete} activityLog={activityLog} />;
      case 'plans':
        return <StudyPlans onBack={() => setCurrentModule('vocab')} />;
      default: 
        return <VocabList currentLevel={selectedLevel} currentLang={currentLang} masteredVocab={masteredVocab} onResetVocab={() => handleResetVocab(selectedLevel)} onStartQuiz={(mode) => handleStartQuiz(mode, 'vocab')} userPlan={userPlan} onUpgradeClick={() => setCurrentModule('plans')} onToggleMastery={handleToggleVocabMastery} activityLog={activityLog} />;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F8FAFC] dark:bg-slate-950 font-sans overflow-hidden transition-colors duration-300">
        <div className="h-20 w-full border-b border-slate-200/60 dark:border-slate-800/60 bg-white/80 dark:bg-slate-900/80 flex items-center justify-between px-6">
          <div className="flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
            <div className="w-32 h-6 bg-slate-200 dark:bg-slate-800 rounded-md hidden sm:block"></div>
          </div>
          <div className="flex gap-4 animate-pulse">
            <div className="w-24 h-10 bg-slate-200 dark:bg-slate-800 rounded-xl"></div>
            <div className="w-32 h-10 bg-slate-200 dark:bg-slate-800 rounded-xl hidden sm:block"></div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto w-full px-6 py-10 flex-grow animate-pulse">
          <div className="flex flex-col items-center mb-10">
            <div className="w-64 h-10 bg-slate-200 dark:bg-slate-800 rounded-xl mb-3"></div>
            <div className="w-48 h-4 bg-slate-200 dark:bg-slate-800 rounded-md"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 place-items-center">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="w-full max-w-[300px] h-[14.5rem] bg-slate-200/70 dark:bg-slate-800/70 rounded-3xl flex flex-col p-5 border border-slate-100 dark:border-slate-700/50 relative overflow-hidden">
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 dark:via-white/10 to-transparent animate-[shimmer_1.5s_infinite]"></div>
                <div className="w-32 h-8 bg-slate-300/50 dark:bg-slate-700/50 rounded-lg mx-auto mb-2"></div>
                <div className="w-20 h-3 bg-slate-300/50 dark:bg-slate-700/50 rounded-md mx-auto mb-auto"></div>
                <div className="w-full h-12 bg-slate-300/50 dark:bg-slate-700/50 rounded-xl mb-2"></div>
                <div className="w-full h-12 bg-slate-300/50 dark:bg-slate-700/50 rounded-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user && !guestMode) {
    // Serve public content pages without requiring login
    const publicPath = window.location.pathname;
    const PUBLIC_PREFIXES = ['/blog', '/contact', '/our-story', '/japan-explorer', '/japan-events', '/onomatopoeia', '/verb-pairs', '/shop', '/study-plans', '/refund-policy', '/privacy-policy', '/terms-of-service'];
    if (PUBLIC_PREFIXES.some(p => publicPath === p || publicPath.startsWith(p + '/'))) {
      const handlePublicSignIn = () => {
        window.history.pushState(null, '', '/');
        setShowAuthForm(true);
        setAuthView('login');
      };
      return (
        <Router>
          <PublicLayout onSignIn={handlePublicSignIn} currentTheme={currentTheme} onThemeChange={setCurrentTheme}>
            <Routes>
              <Route path="/blog" element={<Suspense fallback={null}><BlogPage standalone /></Suspense>} />
              <Route path="/blog/:slug" element={<Suspense fallback={null}><ArticlePage /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />
              <Route path="/our-story" element={<Suspense fallback={null}><OurStoryPage /></Suspense>} />
              <Route path="/japan-explorer" element={<Suspense fallback={null}><JapanExplorerPage /></Suspense>} />
              <Route path="/japan-events" element={<Suspense fallback={null}><JapanEventsPage /></Suspense>} />
              <Route path="/onomatopoeia" element={<Suspense fallback={null}><OnomatopoeiaPage currentLang={currentLang} /></Suspense>} />
              <Route path="/verb-pairs" element={<Suspense fallback={null}><VerbPairsPage currentLang={currentLang} /></Suspense>} />
              <Route path="/shop" element={<Suspense fallback={null}><ShopPage /></Suspense>} />
              <Route path="/study-plans" element={<Suspense fallback={null}><StudyPlans onBack={() => window.history.back()} onAuthRequired={handlePublicSignIn} /></Suspense>} />
              <Route path="/refund-policy" element={<Suspense fallback={null}><RefundPolicyPage /></Suspense>} />
              <Route path="/privacy-policy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
              <Route path="/terms-of-service" element={<Suspense fallback={null}><TermsOfServicePage /></Suspense>} />
            </Routes>
          </PublicLayout>
        </Router>
      );
    }

    if (showPublicPlans) {
      return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans">
          <nav className="sticky top-0 z-50 flex items-center justify-between px-6 h-16 bg-white/80 backdrop-blur-md border-b border-slate-100">
            <div className="text-lg font-black tracking-tighter text-slate-900">GO4<span className="text-rose-600">NIHONGO</span></div>
            <div className="flex items-center gap-3">
              <button onClick={() => { setShowPublicPlans(false); setShowAuthForm(true); setAuthView('login'); }}
                className="text-sm font-black text-slate-500 hover:text-slate-900 transition-colors px-3 py-1.5">Login</button>
              <button onClick={() => { setShowPublicPlans(false); setShowAuthForm(true); setAuthView('signup'); }}
                className="text-sm font-black bg-rose-600 text-white px-5 py-2 rounded-full hover:bg-rose-700 transition-colors">Sign up free →</button>
            </div>
          </nav>
          <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="text-slate-400 font-bold">Loading plans…</div></div>}>
            <StudyPlans
              onBack={() => setShowPublicPlans(false)}
              onAuthRequired={() => { setShowPublicPlans(false); setShowAuthForm(true); setAuthView('signup'); }}
            />
          </Suspense>
        </div>
      );
    }

    if (!showAuthForm) {
      return (
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] dark:bg-slate-950">
            <div className="text-slate-400 font-bold text-sm">Loading…</div>
          </div>
        }>
          <LandingPage
            onGetStarted={() => setShowAuthForm(true)}
            onViewPlans={() => setShowPublicPlans(true)}
            onStartFree={() => {
              setGuestMode(true);
              setSelectedLevel('N5');
              setCurrentView('practice');
              setCurrentModule('vocab');
            }}
            currentTheme={currentTheme}
            onThemeChange={setCurrentTheme}
          />
        </Suspense>
      );
    }

    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 px-6 py-10 transition-colors duration-300">
        <nav className="max-w-md mx-auto flex items-center justify-between mb-10">
          <div className="text-2xl font-black dark:text-white">
            GO4<span className="text-rose-600">NIHONGO</span>
          </div>
          <button
            onClick={() => setShowAuthForm(false)}
            className="text-xs font-black text-slate-400 hover:text-slate-700 transition-colors"
          >← Back</button>
        </nav>
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="text-slate-400 font-bold text-sm">Loading…</div>
          </div>
        }>
          {authView === 'login' ? (
            <Login onSwitchToSignup={() => setAuthView('signup')} redirectError={redirectAuthError} />
          ) : (
            <Signup onSwitchToLogin={() => setAuthView('login')} />
          )}
        </Suspense>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <Router>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col font-sans relative transition-colors duration-300" style={{ background: 'var(--bg-primary)' }}>
        
        <Suspense fallback={null}>
          <CommandPalette
            isOpen={showCommandPalette}
            onClose={() => setShowCommandPalette(false)}
            onNavigate={handleCommandNavigation}
            onSelectLevel={handleLevelSelect}
            isDarkMode={isDarkMode}
            onToggleDark={() => setCurrentTheme(isDarkMode ? 'day' : 'night')}
          />
        </Suspense>

        <AchievementSystem
          isOpen={showAchievements}
          onClose={() => setShowAchievements(false)}
          masteredVocab={masteredVocab}
          masteredKanji={masteredKanji}
          masteredGrammar={masteredGrammar}
          streak={streak}
          reviewCount={reviewCount}
        />

        {AchievementToastEl}

        <Suspense fallback={null}>
          <StudyTimer isOpen={showTimer} onClose={() => setShowTimer(false)} />
          <StrokeCanvas isOpen={showStrokeCanvas} onClose={() => setShowStrokeCanvas(false)} isDarkMode={isDarkMode} />
          <VocabInContext isOpen={showVocabContext} onClose={() => setShowVocabContext(false)} currentLevel={selectedLevel} currentLang={currentLang} />
          <ListeningQuiz isOpen={showListeningQuiz} onClose={() => setShowListeningQuiz(false)} currentLevel={selectedLevel} currentLang={currentLang} masteredVocab={masteredVocab} />
          <SettingsPanel
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            user={user}
            userPlan={userPlan}
            onSignOut={() => signOut(auth)}
            currentLang={currentLang}
            onLangChange={setCurrentLang}
          />
        </Suspense>

        {/* ── Floating theme toggle (all pages except landing) ── */}
        <FloatingThemeBtn currentTheme={currentTheme} onThemeChange={setCurrentTheme} />

        {/* ── Collapsible Tools Toolbar (draggable) ── */}
        {user && (() => {
          const xp = (masteredVocab.length + masteredKanji.length + masteredGrammar.length) * 10;
          const { level: xpLevel } = xpProgress(xp);

          const studyTools = [
            { icon: '🎧', label: 'Listening Quiz',   color: 'bg-blue-600 hover:bg-blue-500',     action: () => setShowListeningQuiz(true) },
            { icon: '📖', label: 'Vocab in Context', color: 'bg-teal-600 hover:bg-teal-500',     action: () => setShowVocabContext(true) },
            { icon: '✍️', label: 'Writing Practice', color: 'bg-indigo-600 hover:bg-indigo-500', action: () => setShowStrokeCanvas(true) },
          ];
          const utilityTools = [
            { icon: '⏱️', label: 'Study Timer',       color: 'bg-orange-500 hover:bg-orange-400',  action: () => setShowTimer(true) },
            { icon: '🏆', label: `Achievements · Lv${xpLevel}`, color: 'bg-violet-700 hover:bg-violet-600', action: () => setShowAchievements(true) },
            { icon: '📤', label: 'Share Progress',    color: 'bg-indigo-600 hover:bg-indigo-500',  action: () => setShowShareCard(true) },
            { icon: '💡', label: 'App Guide',          color: 'bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600', action: () => setShowWelcome(true) },
          ];

          const ToolBtn = ({ t }) => (
            <button
              onClick={() => { t.action(); setShowToolbar(false); }}
              title={t.label}
              aria-label={t.label}
              className="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl transition-all text-left hover:opacity-80 active:scale-[0.98]"
              style={{ background: 'transparent', color: 'var(--text-primary)' }}
            >
              <span className="text-lg leading-none w-6 text-center shrink-0" aria-hidden="true">{t.icon}</span>
              <span className="text-sm font-bold whitespace-nowrap">{t.label}</span>
            </button>
          );

          const handleDragStart = (e) => {
            const isTouch = e.type === 'touchstart';
            const startX = isTouch ? e.touches[0].clientX : e.clientX;
            const startY = isTouch ? e.touches[0].clientY : e.clientY;
            const startRight  = toolbarPos.right;
            const startBottom = toolbarPos.bottom;
            let moved = false;

            const onMove = (ev) => {
              const cx = isTouch ? ev.touches[0].clientX : ev.clientX;
              const cy = isTouch ? ev.touches[0].clientY : ev.clientY;
              const dx = cx - startX;
              const dy = cy - startY;
              if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
              if (!moved) return;
              setToolbarDragging(true);
              const newRight  = Math.max(8, Math.min(window.innerWidth  - 64, startRight  - dx));
              const newBottom = Math.max(8, Math.min(window.innerHeight - 64, startBottom + dy));
              setToolbarPos({ right: newRight, bottom: newBottom });
            };
            const onUp = () => {
              if (!moved) setShowToolbar(v => !v);
              setToolbarDragging(false);
              window.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
              window.removeEventListener(isTouch ? 'touchend'  : 'mouseup',   onUp);
            };
            window.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove, { passive: false });
            window.addEventListener(isTouch ? 'touchend'  : 'mouseup',   onUp);
          };

          return (
            <div
              className="fixed z-40 flex flex-col items-end gap-2"
              style={{ right: toolbarPos.right, bottom: toolbarPos.bottom }}
            >
              {showToolbar && (
                <div
                  className="mb-2 w-52 rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
                  style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-out)' }}
                >
                  {/* Study group */}
                  <div className="px-3 pt-3 pb-1">
                    <p className="text-[8px] font-black uppercase tracking-widest px-1 mb-1" style={{ color: 'var(--text-muted)' }}>Study</p>
                    {studyTools.map((t, i) => <ToolBtn key={i} t={t} />)}
                  </div>
                  {/* Divider */}
                  <div className="mx-4" style={{ height: 1, background: 'var(--border-color)' }} />
                  {/* Utility group */}
                  <div className="px-3 pt-1 pb-3">
                    <p className="text-[8px] font-black uppercase tracking-widest px-1 mb-1 mt-2" style={{ color: 'var(--text-muted)' }}>Tools</p>
                    {utilityTools.map((t, i) => <ToolBtn key={i} t={t} />)}
                  </div>
                </div>
              )}

              {/* Neumorphic toggle button — draggable */}
              <button
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                aria-label={showToolbar ? 'Close tools menu' : 'Open tools menu'}
                aria-expanded={showToolbar}
                style={{
                  width: 56, height: 56,
                  borderRadius: '50%',
                  background: 'var(--bg-primary)',
                  boxShadow: showToolbar ? 'var(--neu-in)' : 'var(--neu-out)',
                  cursor: toolbarDragging ? 'grabbing' : 'grab',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  transition: 'box-shadow 0.2s',
                  userSelect: 'none',
                  touchAction: 'none',
                }}
              >
                <span style={{ fontSize: 20, lineHeight: 1, color: showToolbar ? 'var(--text-muted)' : 'var(--accent)' }} aria-hidden="true">
                  {showToolbar ? '✕' : '⚡'}
                </span>
                {!showToolbar && (
                  <span style={{ fontSize: 7, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', marginTop: 2 }}>
                    Tools
                  </span>
                )}
              </button>
            </div>
          );
        })()}

        {showWelcome && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-12 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm overflow-y-auto animate-in fade-in duration-300">
            <div className="w-full max-w-[1100px] m-auto">
              <Suspense fallback={null}>
                <WelcomeGuide
                  onDismiss={() => { localStorage.setItem('go4nihongo_welcome_seen', '1'); setShowWelcome(false); }}
                  onStartTest={() => {
                    localStorage.setItem('go4nihongo_welcome_seen', '1');
                    setShowWelcome(false);
                    setShowPlacementTest(true);
                  }}
                  currentLang={currentLang}
                />
              </Suspense>
            </div>
          </div>
        )}

        {showPlacementTest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-12 bg-slate-900/60 dark:bg-black/70 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
            <div className="w-full max-w-[800px] m-auto">
              <Suspense fallback={null}>
                <PlacementTest
                  onCancel={() => setShowPlacementTest(false)}
                  onFinish={(recommendedLevel) => {
                    setShowPlacementTest(false);
                    handleLevelSelect(recommendedLevel);
                  }}
                />
              </Suspense>
            </div>
          </div>
        )}

        {showOnboarding && user && (
          <Suspense fallback={null}>
            <Onboarding
              uid={user.uid}
              onComplete={(nick) => {
                setUserNickname(nick);
                setShowOnboarding(false);
              }}
            />
          </Suspense>
        )}

        {paymentStatus === 'success' && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-4 md:px-5 py-3 rounded-2xl shadow-xl border text-sm font-bold animate-in slide-in-from-top-4 fade-in duration-300 w-[calc(100%-32px)] max-w-sm md:w-auto bg-emerald-50 dark:bg-emerald-900/80 border-emerald-200 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200">
            <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>Payment successful! Your plan is now active.</span>
            <button
              onClick={() => setPaymentStatus(null)}
              className="ml-2 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {paymentStatus === 'cancelled' && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-4 rounded-2xl shadow-2xl border animate-in slide-in-from-top-4 fade-in duration-300 w-[calc(100%-32px)] max-w-md bg-amber-50 dark:bg-amber-900/90 border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-100">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black mb-2">Payment didn't go through</p>
                <p className="text-xs font-bold mb-2 text-amber-800 dark:text-amber-200">No charge was made. If you tried to pay and it failed, try one of these:</p>
                <ul className="text-xs font-semibold space-y-1 text-amber-800 dark:text-amber-200 list-disc list-inside">
                  <li>Try a different card, or contact your bank to enable international payments</li>
                  <li>On iPhone or Safari? Open this page in <span className="font-black">Chrome</span> to use Google Pay</li>
                  <li>Indian cards need an OTP step — make sure pop-ups aren't blocked</li>
                </ul>
              </div>
              <button
                onClick={() => setPaymentStatus(null)}
                className="p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <Header
          user={user}
          userNickname={userNickname}
          onSignOut={() => signOut(auth)}
          onLogin={() => { setGuestMode(false); setShowAuthForm(true); setAuthView('login'); }}
          onSignUp={() => { setGuestMode(false); setShowAuthForm(true); setAuthView('signup'); }}
          onNavigate={setCurrentView}
          onNavigateToModule={(mod) => { setCurrentView('practice'); setCurrentModule(mod); }}
          currentView={currentView}
          currentLang={currentLang}
          onLangChange={setCurrentLang}
          streak={streak}
          isDarkMode={isDarkMode}
          currentTheme={currentTheme}
          onThemeChange={setCurrentTheme}
          onToggleDark={() => setCurrentTheme(isDarkMode ? 'day' : 'night')}
          onOpenSettings={() => setShowSettings(true)}
        />

        {/* Guest signup nudge banner */}
        {!user && guestMode && !guestBannerDismissed && (
          <div className="bg-gradient-to-r from-rose-500 to-rose-700 text-white text-sm font-bold px-4 py-2.5 flex items-center justify-center gap-3 shadow-md">
            <span className="text-lg">🔥</span>
            <span className="hidden sm:inline">You're exploring as a guest. Sign up to save your progress, streak, and unlock N4–N1.</span>
            <span className="sm:hidden">Sign up to save your progress</span>
            <button
              onClick={() => { setGuestMode(false); setShowAuthForm(true); setAuthView('signup'); }}
              className="bg-white text-rose-700 px-3 py-1 rounded-full text-xs font-black hover:bg-rose-50 transition-colors whitespace-nowrap"
            >
              Sign up free
            </button>
            <button
              onClick={() => { setGuestBannerDismissed(true); localStorage.setItem('go4nihongo_guest_banner_dismissed', '1'); }}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Guest paywall modal — shown when guest clicks N4+ */}
        {showGuestPaywall && !user && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-8 relative animate-in zoom-in-95 duration-200">
              <button
                onClick={() => setShowGuestPaywall(false)}
                className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>

              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 text-white text-2xl font-black shadow-lg mb-4">
                  {paywallLevel || 'N4'}
                </div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{paywallLevel || 'This level'} is premium</h2>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
                  N5 is 100% free forever. Unlock N4 → N1 with a Premium plan — from <span className="font-black text-rose-600">$4.25/month</span> or lifetime from <span className="font-black text-rose-600">$42</span>.
                </p>
              </div>

              <ul className="space-y-2.5 mb-6 text-sm text-slate-600 dark:text-slate-300">
                {['Unlock N4 → N1 vocab, kanji & grammar', 'SRS spaced repetition', 'All JLPT drill types', 'Reading practice & mock tests'].map(item => (
                  <li key={item} className="flex items-center gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center text-xs font-black shrink-0">✓</span>
                    <span className="font-semibold">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setShowGuestPaywall(false); setGuestMode(false); setShowPublicPlans(true); }}
                  className="w-full py-3 bg-rose-600 text-white rounded-xl font-black text-sm hover:bg-rose-700 transition-colors shadow-lg shadow-rose-200 dark:shadow-rose-900/50"
                >
                  See plans →
                </button>
                <button
                  onClick={() => { setShowGuestPaywall(false); setGuestMode(false); setShowAuthForm(true); setAuthView('login'); }}
                  className="w-full py-2.5 text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  Already a member? Log in
                </button>
                <button
                  onClick={() => setShowGuestPaywall(false)}
                  className="w-full py-2.5 text-xs font-bold text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                >
                  Keep exploring N5 for free
                </button>
              </div>
            </div>
          </div>
        )}

        <main className={`flex-grow ${(currentModule === 'plans' || window.location.pathname === '/study-plans') ? '' : 'pb-20'}`}>
          <Routes>
            <Route path="/" element={
              currentView === 'dashboard' ? (
                <div className="animate-in fade-in duration-500">
                  <Suspense fallback={<ModuleLoader />}>
                    <Dashboard onSelectLevel={handleLevelSelect} masteredVocab={masteredVocab} masteredKanji={masteredKanji} masteredGrammar={masteredGrammar} masteredReading={masteredReading} readingAttempts={readingAttempts} srsProgress={srsProgress} drillProgress={drillProgress} quizScores={quizScores} activityLog={activityLog} totalStudySecs={totalStudySecs} onStartReview={(level) => { handleLevelSelect(level); setCurrentModule('review'); }} onStartDrill={(level) => { handleLevelSelect(level); setCurrentModule('drill'); }} user={user} userNickname={userNickname} />
                  </Suspense>
                </div>
              ) : (
                <div className="animate-in slide-in-from-bottom-4 duration-500">
                  <div className="sticky top-14 md:top-20 z-40 bg-[#F8FAFC] dark:bg-slate-950 border-b border-slate-200/60 dark:border-slate-800 transition-colors">
                  <div className="max-w-[1400px] mx-auto px-3 md:px-4 py-1 md:py-2 flex flex-wrap md:flex-nowrap items-center gap-1.5 md:gap-3">

                    <button onClick={() => setCurrentView('dashboard')} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-900 dark:hover:text-white font-bold transition-all text-xs shrink-0">
                      ← Back
                    </button>

                    {selectedLevel !== 'Extras' && currentModule !== 'plans' && (
                      <>
                        {/* Study module tabs — single row on mobile, flex row on md+ */}
                        <div className="grid grid-cols-4 md:flex bg-slate-100 dark:bg-slate-800/50 p-0.5 md:p-1 rounded-xl shrink-0 transition-colors gap-0.5 md:gap-0">
                          {['vocab', 'kanji', 'grammar', 'reading'].map((mod) => (
                            <button
                              key={mod}
                              onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' }); setCurrentModule(mod); }}
                              className={`capitalize text-[10px] md:text-xs font-black px-1.5 md:px-3 py-1 md:py-1.5 rounded-lg transition-all whitespace-nowrap ${currentModule === mod ? 'bg-white dark:bg-slate-700 text-rose-600 dark:text-rose-400 shadow-sm' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                            >
                              {mod}
                            </button>
                          ))}
                        </div>

                        <div className="flex items-center gap-1.5 md:gap-2 md:ml-auto shrink-0 flex-wrap md:flex-nowrap">
                          {[['drill', 'quiz']].map((group, groupIndex) => (
                            <div key={groupIndex} className="flex bg-slate-100 dark:bg-slate-800/50 p-1 rounded-xl shrink-0 transition-colors">
                              {group.filter(mod => !(mod === 'quiz' && currentModule === 'reading')).map((mod) => (
                                <button
                                  key={mod}
                                  onClick={() => {
                                    if (mod === 'quiz') {
                                      if (currentModule === 'kanji') setQuizSubject('kanji');
                                      else if (currentModule === 'vocab') setQuizSubject('vocab');
                                      else if (currentModule === 'grammar') setQuizSubject('grammar');
                                    }
                                    setCurrentModule(mod);
                                  }}
                                  className={`capitalize text-[10px] md:text-xs font-black px-1.5 md:px-3 py-1 md:py-1.5 rounded-lg transition-all whitespace-nowrap ${currentModule === mod ? 'bg-white dark:bg-slate-700 text-rose-600 dark:text-rose-400 shadow-sm' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                                >
                                  {mod}
                                </button>
                              ))}
                            </div>
                          ))}

                          {/* Review button with due-count badge */}
                          {(() => {
                            const now = Date.now();
                            const ld = srsProgress[selectedLevel] || {};
                            const due = ['vocab','kanji','grammar'].reduce((n, t) =>
                              n + Object.values(ld[t] || {}).filter(item =>
                                item.status === 'mastered' && (!item.dueDate || item.dueDate <= now)
                              ).length, 0);
                            return (
                              <div className="relative shrink-0">
                                <button
                                  onClick={() => setCurrentModule('review')}
                                  className={`text-xs font-black px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${
                                    currentModule === 'review'
                                      ? 'bg-violet-600 text-white shadow-sm'
                                      : due > 0
                                        ? 'bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-900/50'
                                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                                  }`}
                                >
                                  Review
                                </button>
                                {due > 0 && currentModule !== 'review' && (
                                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center leading-none">
                                    {due > 99 ? '99+' : due}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </>
                    )}

                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden lg:block">
                        {selectedLevel === 'Extras' ? 'Extras' : `JLPT ${selectedLevel}`}
                      </span>
                    </div>

                  </div>
                  </div>{/* end sticky tab bar */}

                  <div className="mt-4">
                    <ErrorBoundary minimal title="This module failed to load">
                      <Suspense fallback={<ModuleLoader />}>
                        {renderModule()}
                      </Suspense>
                    </ErrorBoundary>
                  </div>

                </div>
              )
            } />

            <Route path="/study-plans" element={<Suspense fallback={<ModuleLoader />}><StudyPlans onBack={() => window.history.back()} /></Suspense>} />
            <Route path="/refund-policy" element={<Suspense fallback={null}><RefundPolicyPage /></Suspense>} />
            <Route path="/privacy-policy" element={<Suspense fallback={null}><PrivacyPolicyPage /></Suspense>} />
            <Route path="/terms-of-service" element={<Suspense fallback={null}><TermsOfServicePage /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />
            <Route path="/our-story" element={<Suspense fallback={null}><OurStoryPage /></Suspense>} />
            <Route path="/blog" element={<Suspense fallback={null}><BlogPage standalone /></Suspense>} />
            <Route path="/blog/:slug" element={<Suspense fallback={null}><ArticlePage /></Suspense>} />
            <Route path="/japan-explorer" element={<Suspense fallback={null}><JapanExplorerPage /></Suspense>} />
            <Route path="/japan-events" element={<Suspense fallback={null}><JapanEventsPage /></Suspense>} />
            <Route path="/dictionary" element={<Suspense fallback={<ModuleLoader />}><MyDictionary /></Suspense>} />
            <Route path="/onomatopoeia" element={<Suspense fallback={<ModuleLoader />}><OnomatopoeiaPage currentLang={currentLang} /></Suspense>} />
            <Route path="/verb-pairs" element={<Suspense fallback={<ModuleLoader />}><VerbPairsPage currentLang={currentLang} /></Suspense>} />
            <Route path="/shop" element={<Suspense fallback={null}><ShopPage /></Suspense>} />

          </Routes>
        </main>

        <Footer 
          onNavigateToModule={(mod) => {
            setCurrentView('practice'); 
            setCurrentModule(mod);      
          }} 
        />

      </div>
    </Router>

    {/* Redesign announcement banner */}
    <RedesignBanner />

    {/* PWA install prompt */}
    {user && <InstallBanner />}

    {/* Streak protection banner */}
    {user && (
      <StreakBanner
        streak={streak}
        lastActiveDate={lastActiveDate}
        onStudyNow={() => setCurrentView('practice')}
      />
    )}

    {/* Share progress card */}
    <Suspense fallback={null}>
      <ShareProgressCard
        isOpen={showShareCard}
        onClose={() => setShowShareCard(false)}
        masteredVocab={masteredVocab}
        masteredKanji={masteredKanji}
        masteredGrammar={masteredGrammar}
        streak={streak}
        xpLevel={xpProgress((masteredVocab.length + masteredKanji.length + masteredGrammar.length) * 10).level}
        xp={(masteredVocab.length + masteredKanji.length + masteredGrammar.length) * 10}
      />
    </Suspense>

    </ErrorBoundary>
  );
}

export default App;